/* eslint-disable camelcase */
import * as Sentry from '@sentry/vue';
import { defineStore } from 'pinia';
import { isEmptyValue, objectIsEmpty, prettifyMixedCase } from '@frontend/shared/Util.js';
import { expandPathAncestors, flattenSchema, getValueAtPath, resolveRefs } from '@frontend/shared/SchemaUtil.js';
import { useDossierStore } from './dossierStore.js';

export const useClientPortalStore = defineStore('portal', {
  state: () => ({
    advisorInfo: null,
    firmInfo: null,
    checklistData: {
      sections: {},
      sectionsList: [],
    },
    _checklistIntervalId: null,
    pollingAttempts: 0,
    pollingInterval: 3000,
    maxPollingAttempts: 100, // Poll for 5 minutes
    checklistPreview: null,
    currentDocumentNumber: 0,
    currentEntityId: '',
    advisorSlug: '',
    firmSlug: '',
    currentStep: 'loading',
    portalContext: null,
    advisorView: false,
    clientInfo: {
      user: null,
      entity: null,
    },
    firstDocument: null,
    isLoading: false,
    hasFetched: false,
    isLoggedIn: false,
    isPolling: false,
    isPublicPreview: false,
    isSubmitting: false,
    shouldRedirectLogin: false,
    loadingMessage: 'Loading your document portal…',
    showManagementDrawer: false,
    uploads: [],
    dossier: {},
    dossierTemplate: {},
    dossierTemplateId: null,
    allFields: [],
    portalRequest: {
      external_id: null,
      ask_for_dossier: false,
      ask_for_documents: false,
      ask_for_questionnaires: false,
      questionnaire_types: [],
      fields: [],
      status: 'draft',
    },
  }),

  getters: {
    clientName: state => {
      return (
        state.clientInfo?.user?.name || state.clientInfo?.entity?.name || ''
      );
    },

    advisorName: state => {
      return state.advisorInfo?.name || '';
    },

    checklistSections: state => {
      const list = state.checklistData?.sectionsList ?? [];

      if (list.length > 0) {
        return list.sort((a, b) => {
          if (a.key === 'other') return 1;
          if (b.key === 'other') return -1;
          return 0;
        });
      }

      return list;
    },

    checklistSectionsExcludingOther: state => {
      return state.checklistSections.filter(
          section => section.key !== 'other',
      );
    },

    selectedDocument(state) {
      return (
        state.getDocumentBySequentialNumber(state.currentDocumentNumber) ?? null
      );
    },

    documentIsSelected: state => (document, section) => {
      const selected = state.selectedDocument;
      if (!selected?.name || !selected?.sectionKey) return false;

      return (
        selected.name === document.name && selected.sectionKey === section.key
      );
    },

    getDocumentBySequentialNumber: state => number => {
      for (const section of state.checklistSections || []) {
        for (const document of section.documents) {
          if (document.sequentialNumber === number) {
            return document;
          }
        }
      }
      return null;
    },

    getDocumentBySectionAndName: state => (sectionKey, documentName) => {
      for (const section of state.checklistSections) {
        if (section.key === sectionKey) {
          return (
            section.documents.find(doc => doc.name === documentName) || null
          );
        }
      }
      return null;
    },

    otherDocuments: state => {
      const otherSection = state.checklistSections.find(
          section => section.key === 'other',
      );

      return otherSection ?
        otherSection.documents.filter(doc => doc.document != null) :
        [];
    },

    allowedPathStrings: state => {
      const allowed = [
        ...new Set(
            state.portalRequest?.fields
                .map(f => f.pathString)
                .filter(Boolean),
        ),
      ];

      return expandPathAncestors(allowed);
    },

    // Preview Data
    requestedDocuments: () => [
      { name: 'Mortgage Statement' },
      { name: 'Life Insurance Policy' },
      { name: 'Tax Form 1040' },
    ],

    csrfToken: () => {
      return (
        document.querySelector('[name=csrfmiddlewaretoken]')?.value ||
        window?.$root?.$data?.csrfToken ||
        ''
      );
    },

    currentStepNumber() {
      return this.currentDocumentNumber;
    },

    totalDocuments(state) {
      return (state.checklistSections || []).reduce(
          (sum, section) => sum + section.documents.length,
          0,
      );
    },

    currentDocumentName(state) {
      return (
        state.getDocumentBySequentialNumber(state.currentDocumentNumber)
            ?.name || 'Document'
      );
    },

    missingFields(state) {
      const dossierStore = useDossierStore();
      const missingFields = new Set();

      state.requiredFields.forEach(fieldPath => {
        const value = getValueAtPath(dossierStore.currentValue, fieldPath);

        if (isEmptyValue(value)) {
          missingFields.add(prettifyMixedCase(fieldPath[0]));
        }
      });

      return [...missingFields];
    },

    dossierFillingIsComplete(state) {
      if (!state.portalRequest) return false;
      if (!state.portalRequest.ask_for_dossier) return false;
      return state.missingFields.length === 0;
    },

    documentsIsComplete(state) {
      // portal request must exist and ask for documents
      if (!state.portalRequest) return false;
      if (!state.portalRequest.ask_for_documents) return false;

      // No sections → complete
      if (state.checklistSectionsExcludingOther.length === 0) return true;

      const allDocuments = state.checklistSections.flatMap(
          section => section.documents || [],
      );

      // total documents must be > 0
      if (allDocuments.length === 0) return false;

      // all docs must NOT be pending or failed
      return allDocuments.every(
          doc => doc.status !== 'pending' && doc.status !== 'failed',
      );
    },

    processComplete(state) {
      return state.dossierFillingIsComplete && state.documentsIsComplete;
    },

    requiredFields(state) {
      return state.portalRequest?.fields.filter(c => c.isScalar && c.required).map(c => c.path);
    },

    menuSections(state) {
      const dossierStore = useDossierStore();

      const items = [
        {
          id: 'welcome',
          label: 'Welcome',
          type: 'welcome',
          icon: 'fas fa-home',
        },
      ];

      let sections = [];

      let documents = [];

      if (state.portalRequest?.ask_for_dossier) {
        const uniqueSections = [
          ...new Set(state.portalRequest?.fields.map(item => item.section)),
        ];

        sections = uniqueSections.map(id => {
          const sectionFields = state.portalRequest?.fields.filter(c => c.section === id);
          const requiredCount= sectionFields.filter(c => c.isScalar && c.required).length;

          const missingFields = new Set();

          sectionFields
              .filter(c => c.isScalar && c.required) // only required scalar fields count as "missing"
              .forEach(field => {
                const value = getValueAtPath(dossierStore.currentValue, field.path); // <-- use path
                if (isEmptyValue(value)) missingFields.add(field.path); // <-- stable key
              });

          const missingCount = missingFields.size;
          const icon = 'fas fa-arrow-right';
          const count = requiredCount > 0 ? `${requiredCount - missingCount}/${requiredCount}` : null;

          return {
            id,
            label: prettifyMixedCase(id),
            type: 'section',
            count,
            requiredCount,
            icon,
          };
        });
      }

      if (state.portalRequest?.ask_for_documents) {
        documents = [
          {
            id: 'documents',
            label: 'Documents',
            type: 'documents',
            icon: 'fas fa-folder-open',
          },
        ];
      }

      let questionnaires = [];

      if (state.portalRequest?.ask_for_questionnaires) {
        questionnaires = [
          {
            id: 'questionnaires',
            label: 'Questionnaires',
            type: 'questionnaires',
            icon: 'fas fa-question',
          },
        ];
      }

      return [...items, ...sections, ...documents, ...questionnaires];
    },
  },

  actions: {
    async initializePortal(params) {
      this.isLoading = true;

      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlEntityId = urlParams.get('entityExternalId') || '';
        const urlAdvisorSlug = urlParams.get('advisor') || '';
        const urlFirmSlug = urlParams.get('firm') || '';

        this.currentEntityId = urlEntityId || params.entityExternalId;
        this.advisorSlug = urlAdvisorSlug || params.advisorSlug; // TODO: Added this, make sure it works
        this.firmSlug = urlFirmSlug || params.firmSlug;
        this.isPublicPreview = window.location.pathname.includes('/visitor/');
        this.advisorView = params.advisorView ?? false;

        try {
          await this.fetchClientData();
        } catch (error) {
          console.error(`Error fetching client portal data`, error);
          return; // exit because we need all the data to continue
        } finally {
          this.hasFetched = true;
        }

        const viewType = this.portalContext.user?.view_type; // advisor or client view based on who the user is
        const user = this.portalContext.user;

        if (viewType === 'client' && !user.is_authenticated) {
          // not allowed—send them to login
          window.location.href = `/account/login?next=${encodeURIComponent(
              window.location.pathname,
          )}`;
          return; // stop init
        }

        if (viewType === 'advisor') {
          // advisor sees management drawer
          this.advisorView = true;
          this.isLoggedIn = true;
        } else if (viewType === 'client' && user.is_authenticated) {
          // client—nothing extra needed, already have clientInfo from context
        } else if (viewType === 'authenticated') {
          // some other logged-in user—but no portal relationship
          // you can choose to redirect or treat them as unauthenticated:
          // you can choose to redirect or treat them as unauthenticated.
          // This should not happen but can if the entity has no associated user.
          // We're sending a Sentry to track which client accounts have not entity associated.

          const userEmail = user.user_info.email || '';
          let message = `User with email ${userEmail} tried to access client portal of entity ${this.currentEntityId}`;

          const targetClientEmail = pc.client?.email || '';
          if (targetClientEmail !== '') {
            message += ` whose email is ${targetClientEmail}`;
          }

          message += `. Their advisor is ${pc.advisor.name} (${pc.advisor.email}).`;

          Sentry.captureMessage(message, { level: 'warning' });

          window.location.href = '/account/login';
          return;
        }

        if (this.currentEntityId && user.is_authenticated) {
          console.log('Loading checklist data');
          await this.loadChecklistData();
        }

        this.currentStep = 'welcome';
      } catch (error) {
        console.error('Error initializing portal:', error);
        this.currentStep = 'welcome';
      } finally {
        this.isLoading = false;
      }
    },
    async fetchDossierTemplate() {
      try {
        const response = await fetch('/api/v1/dossier/template/');
        const data = await response.json();
        const resolvedSchema = resolveRefs(data.schema, data.schema);
        this.dossierTemplate = resolvedSchema;
        this.setupAllFields(resolvedSchema);
      } catch (error) {
        console.error(`Error fetching dossier template`, error);
        Sentry.captureException(error);
      }
    },
    async fetchDossier(entityExternalId) {
      try {
        const response = await fetch(`/api/v1/dossier/entity/${entityExternalId}/`);
        const data = await response.json();

        if (!data.template || isEmptyValue(data.template)) {
          await this.fetchDossierTemplate();
        } else {
          const resolvedSchema = resolveRefs(data.template, data.template);
          this.dossierTemplate = resolvedSchema;
          this.setupAllFields(resolvedSchema);
        }

        if (data.dossier) {
          this.dossier = data.dossier;
        }
      } catch (error) {
        console.error(`Error fetching dossier`, error);
        Sentry.captureException(error);
      }
    },
    async fetchPortalContext(firmSlug, advisorSlug, entityExternalId) {
      // build the URL segments
      const url = `/api/v1/client-portal/context/${firmSlug}/${advisorSlug}/${entityExternalId}`;

      try {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`Status ${res.status}`);

        const context = await res.json();
        this.portalContext = context;

        // user context
        const u = context.user || {};
        const c = context.client || {};
        this.isLoggedIn = u.is_authenticated;
        this.showManagementDrawer = u.show_management_drawer;
        this.advisorView = u.view_type === 'advisor';

        // clientInfo shape — user is the authenticated user, entity is the portal entity
        this.clientInfo = {
          user: u.user_info || c || null,
          entity: c.entity || null,
        };
        // advisor & firm info from context
        this.advisorInfo = context.advisor || null;
        this.firmInfo = context.firm || null;

        // ensure currentEntityId is set
        if (entityExternalId) {
          this.currentEntityId = entityExternalId;
        } else if (c.entity?.external_id) {
          this.currentEntityId = c.entity.external_id;
        } else if (c?.external_id) {
          // only if "client" itself is the entity
          this.currentEntityId = c.external_id;
        }

        if (u.entity?.dossier?.dossier_data) {
          this.dossier = u.entity.dossier.dossier_data;
        }

        if (u.entity?.dossier_template) {
          this.dossierTemplate = resolveRefs(u.entity.dossier_template, u.entity.dossier_template);
        }

        if (u.entity?.dossier_template_id) {
          this.dossierTemplateId = u.entity.dossier_template_id;
        }
      } catch (error) {
        console.error('fetchPortalContext error:', error);
        throw error;
      }
    },

    async fetchClientData() {
      await this.fetchPortalContext(
          this.firmSlug,
          this.advisorSlug,
          this.currentEntityId,
      );
      await this.fetchClientPortalRequest(this.currentEntityId);
    },

    // Used for the setup view by the advisor
    async fetchConfigurationData(firmSlug, advisorSlug, entityExternalId) {
      this.isLoading = true;

      try {
        await this.fetchDossier(entityExternalId);
        await this.fetchDossierTemplate();
        await this.fetchPortalContext(firmSlug, advisorSlug, entityExternalId);
        await this.fetchEntityPortalRequest(entityExternalId);
      } catch (error) {
        console.error(error);
      } finally {
        this.hasFetched = true;
        this.isLoading = false;
      }
    },

    async loadChecklistData() {
      this.isLoading = true;

      try {
        const response = await fetch(
            `/api/v1/client-portal/checklists/${this.currentEntityId}/`,
        );
        if (response.ok) {
          const data = await response.json();
          // Save the checklist
          this.transformChecklistData(data);

          this.ensureFirstDocumentSelected();
        }
      } catch (error) {
        console.error('Error loading checklist:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async markDocument(itemId, currentStatus, newStatus) {
      if (this.advisorView) {
        return;
      }

      this.isLoading = true;

      // Make it togglable
      const status = currentStatus === newStatus ? 'pending' : newStatus;

      try {
        const response = await fetch(
            `/api/v1/client-portal/checklist-item/${itemId}/status/`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': this.csrfToken,
              },
              body: JSON.stringify({
                status,
              }),
            },
        );

        if (response.ok) {
          await response.json();
        }
      } catch (error) {
        console.error('Error loading checklist:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async createPortalRequest(payload) {
      this.managePortalRequests({ method: 'POST', payload });
    },

    async managePortalRequests({ method = 'GET', payload }) {
      if (method === 'POST' && !payload) {
        throw new Error('Method requires a payload');
      }

      this.isLoading = true;

      try {
        const response = await fetch(
            `/api/v1/client-portal/portal-requests/manage/`,
            {
              method,
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': this.csrfToken,
              },
              body: method === 'POST' ? JSON.stringify(this.updateRequestPayload(payload)) : undefined,
            },
        );

        if (response.ok) {
          const json = await response.json();
          this.updateLocalPortalRequest(json);
        }
      } catch (error) {
        console.error('Error managing portal request:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchEntityPortalRequest(entityExternalId) {
      if (!entityExternalId) {
        throw new Error('Method requires an entity external ID');
      }

      this.isLoading = true;

      try {
        const response = await fetch(
            `/api/v1/client-portal/portal-requests/manage/entity/${entityExternalId}/`,
            {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': this.csrfToken,
              },
            },
        );

        if (response.ok) {
          const json = await response.json();
          this.updateLocalPortalRequest(json);
          this.allFields = this.mergeFields(this.allFields, json.fields);
        }
      } catch (error) {
        console.error('Error fetching portal request:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchClientPortalRequest(entityExternalId) {
      if (!entityExternalId) {
        throw new Error('Method requires an entity external ID');
      }

      this.isLoading = true;

      try {
        const response = await fetch(
            `/api/v1/client-portal/portal-requests/client/entity/${entityExternalId}/`,
            {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': this.csrfToken,
              },
            },
        );

        if (response.ok) {
          const json = await response.json();
          this.updateLocalPortalRequest(json);
          this.allFields = this.mergeFields(this.allFields, json.fields);
        }
      } catch (error) {
        console.error('Error fetching portal request:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async updatePortalRequest(payload) {
      if (!payload) {
        throw new Error('Method requires a payload');
      }

      this.isLoading = true;

      try {
        const response = await fetch(
            `/api/v1/client-portal/portal-requests/manage/${this.portalRequest.external_id}/`,
            {
              method: 'PATCH',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': this.csrfToken,
              },
              body: JSON.stringify(this.updateRequestPayload(payload)),
            },
        );

        if (response.ok) {
          const json = await response.json();
          this.updateLocalPortalRequest(json);
        }
      } catch (error) {
        console.error('Error updating portal request:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async submitPortalRequest(dossier_data) {
      this.isSubmitting = true;

      try {
        const response = await fetch(
            `/api/v1/client-portal/portal-requests/client/${this.portalRequest.external_id}/`,
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': this.csrfToken,
              },
              body: JSON.stringify({
                dossier_data,
                dossier_template_id: this.checklistData?.dossier_template_id || this.dossierTemplateId,
              }),
            },
        );

        if (response.ok) {
          this.portalRequest.status = 'completed';
        }
      } catch (error) {
        console.error('Error submitting portal request:', error);
      } finally {
        this.isSubmitting = false;
      }
    },

    updateRequestPayload(payload) {
      return {
        ...this.portalRequest,
        ...payload,
      };
    },

    updateLocalPortalRequest(json) {
      const {
        external_id,
        fields,
        status,
        ask_for_documents,
        ask_for_dossier,
        ask_for_questionnaires,
        questionnaire_types,
      } = json;

      Object.assign(this.portalRequest, {
        external_id,
        fields,
        status,
        ask_for_documents,
        ask_for_dossier,
        ask_for_questionnaires,
        questionnaire_types,
      });
    },

    setupAllFields(resolvedSchema) {
      let flattenedItems = [];

      for (const [key, prop] of Object.entries(resolvedSchema.properties)) {
        const items = flattenSchema(key, prop, this.dossier, [key]);
        flattenedItems = [...flattenedItems, ...items];
      }

      this.allFields = flattenedItems;
    },

    mergeFields(allFields, selectedFields) {
      const selectedByPath = new Map(
          selectedFields.map(f => [f.pathString, f]),
      );

      return allFields.map(field => {
        const override = selectedByPath.get(field.pathString);
        return override ?
          { ...field, ...override } :
          field;
      });
    },

    startPollingChecklist() {
      if (
        this._checklistIntervalId ||
        this.pollingAttempts >= this.maxPollingAttempts
      ) {
        return;
      }

      this.pollingAttempts = 0; // reset count
      this.loadChecklistData();

      this._checklistIntervalId = setInterval(() => {
        this.pollingAttempts++;

        if (this.pollingAttempts >= this.maxPollingAttempts) {
          this.stopPollingChecklist();
          console.warn('Max polling attempts reached. Stopping polling.');
          return;
        }

        this.loadChecklistData();
      }, this.pollingInterval);

      this.isPolling = true;
    },

    stopPollingChecklist() {
      if (this._checklistIntervalId) {
        clearInterval(this._checklistIntervalId);
        this._checklistIntervalId = null;
        this.isPolling = false;
        this.uploads = [];
      }
    },

    returnHome() {
      this.currentStep = 'welcome';
      this.currentDocumentNumber = 0;
    },

    getTotalDocumentsCount(sections = {}) {
      return Object.values(sections).reduce(
          (total, section) => total + section.items.length,
          0,
      );
    },

    transformChecklistData(data) {
      console.log(' transformChecklistData', data);
      if (!data?.sections) {
        console.log('NO SECTIONS IN CHECKLIST DATA');
        this.checklistData = { ...data, sectionsList: [] };
        return;
      }

      // Ensure checklistData and sectionsList are initialized
      if (!this.checklistData?.sectionsList) {
        this.checklistData = { ...data, sectionsList: [] };
      }

      let documentNumber = 1;

      // Check if any document status or the documents count has changed so we can stop polling
      let anyStatusChanged = false;

      const currentSections = Object.keys(
          this.checklistData.sections,
      ).sort();
      const newSections = Object.keys(data.sections).sort();
      const otherHasBeenAdded =
        !currentSections.includes('other') &&
        newSections.includes('other');
      const otherHasBeenRemoved =
        currentSections.includes('other') &&
        !newSections.includes('other');

      // Keep a lookup of previous docs to detect status changes
      const documentsCountHasChanged =
        this.getTotalDocumentsCount(this.checklistData.sections) !==
        this.getTotalDocumentsCount(data.sections);

      const prevList = this.checklistData.sectionsList || [];
      const prevDocIndex = new Map();

      for (const prevSection of prevList) {
        for (const doc of prevSection.documents || []) {
          prevDocIndex.set(`${prevSection.key}::${doc.name}`, doc);
        }
      }

      const newSectionsList = Object.entries(data.sections)
      // remove sections with no display_name
          .filter(([, section]) => !!section.display_name)
          .map(([key, section]) => {
            const sectionName = section.display_name;
            const documents = [];

            for (const item of section.items || []) {
              const name =
              item.custom_document_name ||
              item.document_type ||
              item.document?.classification_string ||
              'Other Document';

              const newStatus = item.status;
              const completed = newStatus === 'submitted' || newStatus === 'completed';

              // detect status changes vs previous list (by section key + doc name)
              const prevDoc = prevDocIndex.get(`${key}::${name}`);
              if (prevDoc && prevDoc.status !== newStatus) {
                anyStatusChanged = true;
              }

              documents.push({
                ...item,
                name,
                completed,
                status: newStatus,
                sequentialNumber: documentNumber++,
                sectionKey: key,
                sectionName,
                document: item.document,
              });

              // clear uploads for completed items
              if (completed && item.id) {
                this.clearUploaded(item.id);
              }
            }

            return { key, name: sectionName, documents };
          });

      if (
        anyStatusChanged ||
        documentsCountHasChanged ||
        otherHasBeenAdded ||
        otherHasBeenRemoved
      ) {
        this.stopPollingChecklist();
        console.log('Polling stopped due to status change');
      }

      // Optionally merge other fields from `data` into checklistData
      this.checklistData = {
        ...this.checklistData,
        ...data,
        sectionsList: newSectionsList,
      };
    },

    setCurrentStep(step) {
      this.currentStep = step;
    },

    setCurrentDocumentNumber(docNumber) {
      this.currentDocumentNumber = docNumber;
    },

    isCurrentDocument(sequentialNumber) {
      return this.currentDocumentNumber === sequentialNumber;
    },

    selectDocument(sequentialNumber) {
      const newSelection = this.getDocumentBySequentialNumber(sequentialNumber);

      if (newSelection) {
        this.currentDocumentNumber = newSelection.sequentialNumber;
        this.currentStep = 'upload';
      }
    },

    selectFirstDocument() {
      this.currentDocumentNumber = 1;
    },

    markUploaded() {
      const targetDocument = this.selectedDocument;

      if (targetDocument) {
        if (!this.uploads.includes(targetDocument.id)) {
          this.uploads.push(targetDocument.id);
        }
      }
    },

    clearUploaded(documentId = null) {
      if (documentId) {
        // Remove specific document ID from uploads array
        const index = this.uploads.indexOf(documentId);
        if (index > -1) {
          this.uploads.splice(index, 1);
        }
      } else {
        // Clear current document if no ID specified
        const targetDocument = this.selectedDocument;
        if (targetDocument) {
          const index = this.uploads.indexOf(targetDocument.id);
          if (index > -1) {
            this.uploads.splice(index, 1);
          }
        }
      }
    },

    moveToNextDocument() {
      if (
        this.currentDocumentNumber === this.totalDocuments &&
        this.documentsIsComplete
      ) {
        this.currentStep = 'complete';

        return { completed: true };
      } else {
        this.currentDocumentNumber++;

        const nextDoc = this.getDocumentBySequentialNumber(
            this.currentDocumentNumber,
        );

        return { completed: false, document: nextDoc };
      }
    },

    moveToStart() {
      this.currentStep = 'welcome';
    },

    moveToEnd() {
      if (this.processComplete) {
        this.currentStep = 'complete';
      }
    },

    updateChecklistItemStatus(sectionKey, documentName, newStatus) {
      const section = this.checklistData?.sections?.[sectionKey];
      if (!section || !section.items) return;
      section.items.forEach(item => {
        const matches =
          item.document_type === documentName ||
          item.custom_document_name === documentName;
        if (matches) item.status = newStatus;
      });
    },

    login() {
      if (this.advisorView) {
        return;
      }
      window.location.href = '/account/magic-link-login/';
    },
    logout() {
      if (this.advisorView) {
        return;
      }
      window.location.href = '/account/client-logout/';
    },

    handleStartDocumentProcess() {
      if (this.checklistSections.length === 0) {
        this.currentStep = 'others';
        return;
      }

      this.currentStep = 'upload';
      this.selectFirstDocument();
    },

    requestLoginRedirect() {
      console.log('⚡️ requestLoginRedirect called');
      this.shouldRedirectLogin = true;
    },
    clearLoginRedirect() {
      this.shouldRedirectLogin = false;
    },

    ensureFirstDocumentSelected() {
      this.currentStep = 'upload';

      // If already selected and valid, keep it
      if (this.currentDocumentNumber && this.selectedDocument) return;

      // Find the first document across all sections (in display order)
      for (const section of this.checklistSections || []) {
        const firstDoc = (section.documents || []).find(d => d?.sequentialNumber);
        if (firstDoc) {
          this.currentDocumentNumber = firstDoc.sequentialNumber;
          this.firstDocument = firstDoc;
          return;
        }
      }

      // Nothing to select
      this.currentDocumentNumber = 0;
      this.firstDocument = null;
    },

    // handleRedirectToLogin() {
    //   this.$router.push({
    //     path: '/account/client-login/',
    //     query: {
    //       entity: this.currentEntityId,
    //       advisor: this.advisorSlug,
    //       firm: this.firmSlug,
    //     }
    //   })
    // },

    handleRedirectToSignup() {
      const query = new URLSearchParams({
        entity: this.currentEntityId,
        advisor: this.advisorSlug,
        firm: this.firmSlug,
      }).toString();
      window.location.href = `/account/register/?${query}`;
    },

    toggleManagementDrawer() {
      this.showManagementDrawer = !this.showManagementDrawer;
    },

    handleInvitationLinkGenerated(data) {
      console.log('Invitation link generated:', data);
      // You could set a toast/flag here if needed
    },
  },
});
